'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'
import type { ReactNode } from 'react'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  CLIENT_AGREEMENT,
  CLIENT_AGREEMENT_PER_YEAR,
  CLIENT_DETAILS,
  DOSSIER_HOURS,
  DOSSIER_HOURS_BY_MEMBER,
  DOSSIER_NR,
  DOSSIER_TASKS,
  euro,
  formatHours,
  teamMember,
  type AgreementLine,
  type TeamInitials,
} from '../demo-data'
import { useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, Tag } from './app-screen'
import styles from './client-dossier-tabs-visual.module.css'
import { useScenario } from './use-scenario'

const SUBJECT = client(DOSSIER_NR)
const DETAILS = CLIENT_DETAILS[DOSSIER_NR]
const MANAGER = teamMember(SUBJECT.manager)
const ADDRESS = DETAILS.addresses[0]

const MONTHLY = CLIENT_AGREEMENT.filter((line) => line.whenKey === 'monthly')
const YEARLY = CLIENT_AGREEMENT.filter((line) => line.whenKey !== 'monthly')

/**
 * The tabs, in the order the application puts them.
 *
 * The strip is the argument. "Elk klantdossier op één plek" is a claim about how much hangs off one
 * client, and a list of clients with a search box — which is what this hero was — proves nothing
 * about it: every system has a search box. Five tabs across one client, each with something real
 * under it, is the claim itself.
 */
const TABS = ['overview', 'planning', 'hours', 'tasks', 'log'] as const
type TabKey = (typeof TABS)[number]

const OPEN_TASKS = DOSSIER_TASKS.filter((task) => task.done < task.total).length

/** The count beside a tab, where the application shows one. */
const TAB_COUNTS: Partial<Record<TabKey, number>> = {
  planning: CLIENT_AGREEMENT.length,
  tasks: OPEN_TASKS,
}

function tabLabel(t: VisualCopy, key: TabKey): string {
  const copy = t.screen.dossierTabs
  return {
    overview: copy.tabOverview,
    planning: copy.tabPlanning,
    hours: copy.tabHours,
    tasks: copy.tabTasks,
    log: copy.tabLog,
  }[key]
}

/**
 * Which tab is open, per beat.
 *
 * The loop visits four of the five. The log has a card of its own further down this page, and a beat
 * here would be the same picture twice; leaving it in the strip is still the point — it is part of
 * what hangs off the client, and the section below delivers it.
 */
const VISITED: readonly TabKey[] = ['overview', 'planning', 'hours', 'tasks']

/**
 * How long each panel holds. The overview holds far longest: it is the resting frame, the one SSR
 * and reduced motion get, and the one a visitor arriving at the page lands on — it should be read,
 * not caught. The three after it are a glance each.
 */
const BEAT_HOLDS = [4200, 2400, 2200, 2200] as const

const REST_BEAT = 0

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the tab being shown. */
function tabAt(beat: number | null): TabKey {
  return VISITED[beat ?? REST_BEAT] ?? VISITED[REST_BEAT]
}

/** The month a yearly obligation falls in, in the language of the page. */
function agreementMonth(t: VisualCopy, line: AgreementLine): string {
  if (line.whenKey === 'monthly') return ''
  const index: Record<Exclude<AgreementLine['whenKey'], 'monthly'>, number> = { jan: 0, sep: 8, okt: 9 }
  return t.monthsShort[index[line.whenKey]] ?? ''
}

/**
 * A card: a heading, ruled off from its contents.
 *
 * This is how the dossier itself is built — every block on the real screen is a heading over what it
 * heads. The application puts a counting line under each ("2 contactpersonen", "Eén adres"); at this
 * size that is a second line of type on every card, and the rows underneath already say how many
 * there are. The heading alone, with the room the count was taking, reads better.
 */
function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.card}>
      <header className={styles.cardHead}>
        <h3 className={styles.cardTitle}>{title}</h3>
      </header>
      <div className={styles.cardBody}>{children}</div>
    </section>
  )
}

/** "Klantnummer: 10010" — the way the dossier writes a field: label, colon, value in bold. */
function Line({ label, value, accent }: { label: string; value: ReactNode; accent?: boolean }) {
  return (
    <p className={styles.line}>
      <span className={styles.lineLabel}>{label}:</span>{' '}
      <b className={clsx(styles.lineValue, accent && styles.lineValueAccent)}>{value}</b>
    </p>
  )
}

function Avatar({ initials }: { initials: TeamInitials }) {
  return (
    <span className={styles.avatar} style={{ background: teamMember(initials).color }}>
      {initials}
    </span>
  )
}

/** One figure with its label under it, as the dossier's totals row sets them. */
function Figure({ value, label, tone }: { value: ReactNode; label: string; tone?: 'accent' | 'signal' }) {
  return (
    <span className={styles.figure}>
      <b
        className={clsx(
          styles.figureValue,
          tone === 'accent' && styles.figureAccent,
          tone === 'signal' && styles.figureSignal,
        )}
      >
        {value}
      </b>
      <span className={styles.figureLabel}>{label}</span>
    </span>
  )
}

/** One line of the arrangement: what is done, by whom, for how long and at what price. */
function AgreementRow({ line, t, month }: { line: AgreementLine; t: VisualCopy; month?: boolean }) {
  return (
    <div className={clsx(styles.agreementRow, month && styles.agreementRowDated)}>
      <span className={styles.agreementLabel}>{line.label}</span>
      {month && <span className={styles.agreementWhen}>{agreementMonth(t, line)}</span>}
      <Avatar initials={line.member} />
      <span className={styles.agreementHours}>
        {formatHours(line.minutes)} {t.word.hoursUnit}
      </span>
      <span className={styles.agreementPrice}>{euro(line.price, true)}</span>
    </div>
  )
}

/**
 * One tab's contents. Every panel is rendered in every frame and stacked on the same area, so the
 * card's height is the tallest of them and nothing below the strip moves as the tabs change.
 */
function Panel({ shown, children }: { shown: boolean; children: ReactNode }) {
  return (
    <div className={clsx(styles.panel, shown && styles.panelShown)} aria-hidden={!shown}>
      {children}
    </div>
  )
}

/**
 * Klantbeheer hero — one client's file, and the tab strip that says how much of the firm's work is
 * filed against it: the details, the recurring work with who does it and what it costs, the hours
 * booked against its budget, and what is still open.
 *
 * `beat === null` (SSR, no-JS, `prefers-reduced-motion: reduce`) rests on the overview, which is the
 * tab the application itself opens on.
 */
export function ClientDossierTabsVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const copy = t.screen.dossierTabs
  const active = tabAt(beat)

  return (
    <AppUiVisual label={copy.caption(SUBJECT.name)} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="client-dossier-tabs" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.word.clients}
            detail={`${SUBJECT.nr} · ${SUBJECT.name}`}
            right={<Tag className="px-1.5 py-[2px] text-[8.5px]">{SUBJECT.form}</Tag>}
          />

          {/* The strip, with the underline travelling between tabs rather than being redrawn under
              each one — the movement is what reads as one file being looked through. */}
          <div className={styles.tabs} role="presentation">
            {TABS.map((key) => {
              const count = TAB_COUNTS[key]
              return (
                <span key={key} className={clsx(styles.tab, key === active && styles.tabActive)}>
                  {tabLabel(t, key)}
                  {count !== undefined && <span className={styles.tabCount}>{count}</span>}
                </span>
              )
            })}
            <span
              className={styles.tabRule}
              style={
                {
                  // Positioned off the tab's index rather than measured, so it cannot fall out of
                  // step with a render.
                  '--tab-index': TABS.indexOf(active),
                  '--tab-count': TABS.length,
                } as React.CSSProperties
              }
              aria-hidden="true"
            />
          </div>

          <div className={clsx(styles.panels, 'min-h-0 flex-1')}>
            <Panel shown={active === 'overview'}>
              <div className={styles.twoUp}>
                <Card title={copy.generalInfo}>
                  <Line label={t.screen.clientRecord.clientNumber} value={SUBJECT.nr} />
                  <Line label={t.screen.clientRecord.email} value={DETAILS.email} accent />
                  <Line label={t.screen.clientRecord.phone} value={DETAILS.phone} accent />
                  <Line label={t.screen.clientDossier.kvkNumber} value={DETAILS.kvk} />
                  <Line label={t.screen.clientRecord.vatNumber} value={DETAILS.btw} />
                  {/* The IBAN is the one line a phone panel still has no room for once the contacts
                      card is under it. The KvK and VAT numbers above it already say the file is
                      filled from the register, which is what the three are here for. */}
                  <span className={styles.wideOnlyLine}>
                    <Line label="IBAN" value={DETAILS.iban} />
                  </span>
                </Card>

                <div className={clsx(styles.stack, styles.stackUneven)}>
                  {/* The colleague who owns the relationship, then the client's own people. Three
                      names in one card rather than two cards of one: they are the same question —
                      who do you talk to about this file — and the card says who is who. */}
                  <Card title={copy.contactDetails}>
                    <span className={styles.person}>
                      <Avatar initials={MANAGER.initials as TeamInitials} />
                      <span className={styles.personText}>
                        <b className={styles.personName}>{MANAGER.name}</b>
                        <span className={styles.sub}>{t.screen.clientRecord.accountManager.toLowerCase()}</span>
                      </span>
                    </span>
                    {DETAILS.contacts.map((contact) => (
                      <span key={contact.name} className={styles.person}>
                        <span className={styles.personDot} aria-hidden="true" />
                        <span className={styles.personText}>
                          <b className={styles.personName}>{contact.name}</b>
                          <span className={styles.sub}>
                            {t.contactRole[contact.role as keyof typeof t.contactRole]}
                          </span>
                        </span>
                      </span>
                    ))}
                  </Card>

                  {/* The address is what the overview gives up on a phone: three cards do not fit
                      257px, and where a client sits is the one of the three that neither identifies
                      the file nor says who to call about it. */}
                  <div className={styles.wideOnly}>
                    <Card title={copy.addressCard}>
                      <p className={styles.line}>
                        <b className={styles.lineValue}>{ADDRESS.street}</b>{' '}
                        <span className={styles.chip}>{ADDRESS.tag}</span>
                      </p>
                      <p className={styles.sub}>{ADDRESS.place}</p>
                    </Card>
                  </div>
                </div>
              </div>
            </Panel>

            <Panel shown={active === 'planning'}>
              {/* One above the other, not side by side: monthly and yearly work is one list read
                  downward, and in two columns the four monthly lines and the three yearly ones each
                  had a column of white beside them. */}
              <div className={styles.stack}>
                <Card title={copy.monthPlanning}>
                  {MONTHLY.map((line) => (
                    <AgreementRow key={line.label} line={line} t={t} />
                  ))}
                </Card>
                <Card title={copy.yearPlanning}>
                  {YEARLY.map((line) => (
                    <AgreementRow key={line.label} line={line} t={t} month />
                  ))}
                </Card>
              </div>
            </Panel>

            <Panel shown={active === 'hours'}>
              <div className={styles.stack}>
                <Card title={copy.hoursCard}>
                  <div className={styles.figures}>
                    <Figure value={DOSSIER_HOURS.total} label={copy.hoursLabel} tone="accent" />
                    <Figure value={DOSSIER_HOURS.billable} label={copy.billable} />
                    <Figure value={DOSSIER_HOURS.nonBillable} label={copy.nonBillable} />
                    <Figure value={`${DOSSIER_HOURS.usedPct}%`} label={copy.ofBudget} tone="signal" />
                  </div>
                  {/* The budget is the point of booking hours against a client at all, so it is
                      drawn rather than stated: a bar filling towards its own limit. */}
                  <span className={styles.budgetTrack}>
                    <span className={styles.budgetFill} style={{ width: `${DOSSIER_HOURS.usedPct}%` }} />
                  </span>
                  <p className={clsx(styles.budgetFoot, styles.wideOnlyLine)}>
                    <span>{euro(DOSSIER_HOURS.billableAmount, true)}</span>
                    <span>{euro(DOSSIER_HOURS.budget)}</span>
                  </p>
                </Card>

                <Card title={copy.perMemberCard}>
                  {DOSSIER_HOURS_BY_MEMBER.map((row) => (
                    <div key={row.member} className={styles.memberRow}>
                      <Avatar initials={row.member} />
                      <span className={styles.memberName}>{teamMember(row.member).name}</span>
                      <span className={styles.memberTrack}>
                        <span className={styles.memberFill} style={{ width: `${row.pct}%` }} />
                      </span>
                      <span className={styles.memberHours}>{row.hours}</span>
                    </div>
                  ))}
                </Card>
              </div>
            </Panel>

            <Panel shown={active === 'tasks'}>
              <div className={styles.stack}>
                <Card title={copy.openTasks}>
                  {DOSSIER_TASKS.map((task) => {
                    const complete = task.done === task.total
                    return (
                      <div key={task.activity} className={styles.taskRow}>
                        <span className={clsx(styles.check, complete && styles.checkDone)}>{complete ? '✓' : ''}</span>
                        <span className="flex min-w-0 flex-col gap-1.5">
                          <span className={clsx(styles.taskLabel, complete && styles.taskLabelDone)}>
                            {task.activity}
                          </span>
                          <span className={styles.taskProgress}>
                            <span className={styles.taskTrack}>
                              <span
                                className={clsx(styles.taskFill, complete && styles.taskFillDone)}
                                style={{ width: `${(task.done / task.total) * 100}%` }}
                              />
                            </span>
                            <span className={styles.sub}>{copy.subtasks(task.done, task.total)}</span>
                          </span>
                        </span>
                        <span className={styles.taskCount}>
                          {task.done}/{task.total}
                        </span>
                      </div>
                    )
                  })}
                </Card>
              </div>
            </Panel>
          </div>

          {/* What the arrangement adds up to over a year — the monthly lines twelve times over. It is
              true of the file rather than of whichever tab is open, so it sits under all of them. */}
          <div className={styles.footer}>
            <span>{copy.agreed}</span>
            <b className={styles.footerAmount}>
              {euro(CLIENT_AGREEMENT_PER_YEAR)} {copy.perYear}
            </b>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
